/**
 * A bound a capability runs under, edited in place — ROADMAP 9.12.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * COMMITS ON ENTER OR BLUR, REVERTS ON ESCAPE, AND NEVER ON EVERY KEYSTROKE
 *
 * These are numbers the order path reads. A cell that PATCHed as it was typed
 * would send `2`, `20`, `200`, `2000` on the way to 20,000 — four writes, of
 * which three are ceilings nobody chose, and a trader placing an order in
 * that window gets one of them.
 *
 * So the field is local text until it is committed, and committing is an
 * explicit act: Enter, or leaving the field. Escape puts back what was there,
 * because the most common correction to a mistyped ceiling is "undo".
 *
 * UNSET IS SHOWN AS A DASH, NOT AS ZERO. Zero is what the column stores and
 * it does not mean "no quantity allowed" — it means "no ceiling of mine, use
 * the compiled-in behaviour". Rendering it as `0` invites an admin to read it
 * as a block, and this screen has to be legible to whoever opens it at 09:20
 * to find out why orders are not going.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'

export function LimitCell({
  value,
  onCommit,
  min,
  max,
  step = 1,
  format,
  unsetHint,
  busy = false,
  disabled = false,
  testId,
}: {
  value: number
  onCommit: (next: number) => void
  min: number
  max: number
  step?: number
  /** How the stored number reads. `0` is unset for the two integer bounds. */
  format: (v: number) => string
  unsetHint: string
  busy?: boolean
  disabled?: boolean
  testId?: string
}) {
  const [editing, setEditing] = useState(false)
  const [text, setText] = useState('')
  const input = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (editing) input.current?.select()
  }, [editing])

  const open = () => {
    if (disabled || busy) return
    setText(String(value))
    setEditing(true)
  }

  const commit = () => {
    setEditing(false)
    const next = Number(text.trim())
    /*
     * A blank, a word, or something out of range leaves the value ALONE. The
     * shared validator would refuse it anyway; refusing here saves the round
     * trip and keeps a typo from painting the row red.
     */
    if (!Number.isFinite(next) || next < min || next > max) return
    if (next === value) return
    onCommit(next)
  }

  if (editing) {
    return (
      <input
        ref={input}
        type="number"
        min={min}
        max={max}
        step={step}
        value={text}
        data-testid={testId ? `${testId}-input` : undefined}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            commit()
          } else if (e.key === 'Escape') {
            e.preventDefault()
            setEditing(false)
          }
        }}
        className={cn(
          'h-6 w-full rounded border border-primary/50 bg-background px-1.5 text-right',
          'text-[12px] font-medium tabular-nums focus:outline-none focus:ring-1 focus:ring-primary',
        )}
      />
    )
  }

  return (
    <button
      type="button"
      onClick={open}
      disabled={disabled || busy}
      title={value === 0 ? unsetHint : 'Click to change'}
      data-testid={testId}
      className={cn(
        'h-6 w-full rounded border border-transparent px-1.5 text-right text-[12px] tabular-nums',
        'transition-colors hover:border-border hover:bg-secondary',
        'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary',
        'disabled:cursor-not-allowed disabled:opacity-45',
        value === 0 ? 'text-muted-foreground' : 'font-medium text-foreground',
      )}
    >
      {format(value)}
    </button>
  )
}
